// src/pages/shippment/StopsSection.jsx
import React, { useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLoadTable } from "../../redux/action/skuTable.action";
import { toast } from "../../components/AppToast";
import { getSearchBySkuPO } from "../../services/asnAPI/asn";
import ItemsTable from "../../components/ItemsTable";

// 🔑 DEDUPE HELPERS
const normalize = (v) =>
    String(v ?? "")
        .trim()
        .toLowerCase();

const buildDedupeKey = ({ sku, po, so }) => {
    if (!sku) return "";
    return [
        normalize(sku),
        normalize(po || "NA"),
        normalize(so || "NA"),
    ].join("::");
};

// Utility functions for ItemsTable
const formatLineNo = (lineNo) => String(lineNo).padStart(4, "0");

const normalizeSoValue = (val) => {
    if (val == null) return null;
    try {
        const s = String(val).trim();
        return s === "" ? null : s;
    } catch {
        return null;
    }
};

// StopItemsTable component - wrapper around ItemsTable for stops
const StopItemsTable = ({
    data,
    stopId,
    stopOrderNumber,
    onDispatchQtyChange,
    createUsing,
    onRemoveSku,
    onSelectionChange,
    onConfirmDelivery,
    onRemoveRow,
    readOnly = false,
    showConfirmButton = false,
}) => {
    // Columns configuration
    const COLUMNS = useMemo(
        () => [
            "lineNo",
            "salesOrder",
            "soDestinationCity",
            "soDestinationState",
            "skuId",
            "description",
            "pack",
            "uom",
            "orderedQty",
            "itemPOTolerance",
            "openQty",
            "dispatchQty",
            "actions",
        ],
        []
    );

    const COLUMN_CONFIGS = {
        lineNo: { label: "Line #", maxWidth: 110 },
        salesOrder: { label: "SO Number", maxWidth: 180 },
        soDestinationCity: { label: "Destination City", maxWidth: 180 },
        soDestinationState: { label: "Destination State", maxWidth: 180 },
        skuId: { label: "SKU ID", maxWidth: 150 },
        description: { label: "Description", minWidth: 150, maxWidth: 600 },
        pack: { label: "Pack", maxWidth: 100 },
        uom: { label: "UOM", maxWidth: 100 },
        orderedQty: { label: "Ordered Qty", maxWidth: 130 },
        itemPOTolerance: { label: "Tolerance", maxWidth: 120 },
        openQty: { label: "Open Qty", maxWidth: 120 },
        dispatchQty: { label: "Dispatch Qty", maxWidth: 130 },
        actions: { label: "Actions", maxWidth: 100 },
    };

    const FILTER_PLACEHOLDERS = {
        lineNo: "Line#",
        salesOrder: "SO",
        soDestinationCity: "City",
        soDestinationState: "State",
        skuId: "SKU",
        description: "Desc",
        pack: "Pack",
        uom: "UOM",
        orderedQty: "Ordered",
        itemPOTolerance: "Tol",
        openQty: "Open",
        dispatchQty: "Disp",
        actions: "",
    };

    // Header label formatter
    const headerLabelFormatter = useMemo(() => {
        return (groupKey, content = []) => {
            // For STOP groups, show stop number and order number
            const stopMatch = String(groupKey ?? "").match(/^STOP_(\d+)$/);
            if (stopMatch) {
                const stopNumber = stopMatch[1];
                let label = `Stop ${stopNumber}`;
                
                // Find unique SO numbers from content
                const uniqueSO = Array.from(
                    new Set(
                        (Array.isArray(content) ? content : [])
                            .map((r) =>
                                normalizeSoValue(
                                    r.salesOrder ??
                                    r.soNumber ??
                                    r.soNumberFromDetails ??
                                    (r.salesOrderId ? String(r.salesOrderId) : r.shipmentNumber ?? null)
                                )
                            )
                            .filter(Boolean)
                    )
                );
                
                // Normalize stopOrderNumber for comparison
                const normalizedOrderNumber = normalizeSoValue(stopOrderNumber);
                
                // If we have SO numbers from content, use those (they're more accurate)
                if (uniqueSO.length > 0) {
                    if (uniqueSO.length === 1) {
                        label += ` — SO: ${uniqueSO[0]}`;
                    } else {
                        label += ` — SOs: ${uniqueSO.join(", ")}`;
                    }
                } else if (stopOrderNumber) {
                    // Only show order number if we don't have SO numbers from content
                    label += ` — Order: ${stopOrderNumber}`;
                }
                
                return label;
            }
            return groupKey;
        };
    }, [stopOrderNumber]);

    // Cell renderer
    const cellRenderer = useMemo(() => {
        return (
            column,
            row,
            originalIndex,
            { readOnly, filterInputStyle, onCellChange, onInputFocus, onInputBlur, groupKey }
        ) => {
            const lineNumberForDisplay = formatLineNo(
                row.lineNo ??
                (row.__originalIndex__ != null
                    ? row.__originalIndex__ + 1
                    : originalIndex + 1)
            );
            const shownSO =
                row.salesOrder ??
                row.soNumberFromDetails ??
                (row.salesOrderId ? String(row.salesOrderId) : row.shipmentNumber ?? "-");
            const visibleSO = normalizeSoValue(shownSO) ?? "-";

            let destCity = row.soDestinationCity ?? null;
            let destState = row.soDestinationState ?? null;
            if (
                (!destCity || !destState) &&
                Array.isArray(row.soDetails) &&
                row.soDetails.length > 0
            ) {
                const sd = row.soDetails[0]?.destination ?? {};
                destCity = destCity ?? (sd?.city ?? sd?.City ?? null);
                destState = destState ?? (sd?.state ?? sd?.State ?? null);
            }

            const display = {
                lineNo: lineNumberForDisplay,
                salesOrder: visibleSO,
                soDestinationCity: destCity ?? "-",
                soDestinationState: destState ?? "-",
                skuId: row.skuId ?? "-",
                description: row.description ?? "-",
                pack: row.pack ?? "-",
                uom: row.uom ?? "EA",
                orderedQty:
                    (row.orderedQty ?? row.expectedQty ?? row.openQty) == null
                        ? "-"
                        : (row.orderedQty ?? row.expectedQty ?? row.openQty),
                itemPOTolerance: row.itemPOTolerance == null ? 0 : row.itemPOTolerance,
                openQty: row.openQty == null ? "-" : row.openQty,
            };

            if (column === "dispatchQty") {
                const valStr =
                    row.dispatchQty == null || row.dispatchQty === 0
                        ? ""
                        : String(row.dispatchQty);
                return (
                    <input
                        type="number"
                        className="form-control form-control-sm"
                        style={filterInputStyle}
                        value={valStr}
                        onChange={(e) => {
                            if (readOnly) return;
                            const v = e.target.value.trim();
                            let numericValue = v === "" ? 0 : Math.max(0, parseInt(v, 10) || 0);

                            // determine order qty
                            const orderQty = Number(
                                row.orderedQty ?? row.expectedQty ?? row.orderQty ?? row.qtyOrdered ?? 0
                            ) || 0;

                            // tolerance
                            const tolerancePercent = Number(
                                row.itemPOTolerance ?? row.itemASNTolerance ?? row.tolerance ?? row.tolerence ?? 0
                            ) || 0;
                            const dispatchedQty = Number(row.shipmentQty ?? 0);
                            let maxAllowed = Number((orderQty + (orderQty * tolerancePercent) / 100).toFixed(6));
                            maxAllowed -= dispatchedQty;
                            if (orderQty > 0 && numericValue > maxAllowed) {
                                numericValue = maxAllowed;
                                toast.warn(
                                    `Total Dispatch Qty exceeds Sales order tolerance`,
                                    { autoClose: 3000 }
                                );
                            }

                            onCellChange("dispatchQty", numericValue);
                        }}
                        onFocus={onInputFocus}
                        onBlur={onInputBlur}
                        disabled={readOnly}
                        placeholder="Enter qty"
                    />
                );
            }

            if (column === "actions") {
                if (readOnly || !onRemoveRow) return null;
                return (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemoveRow(groupKey, originalIndex);
                        }}
                        title="Delete this row"
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                );
            }

            return display[column];
        };
    }, [onRemoveRow, readOnly]);

    // Handle cell change
    const handleCellChange = useCallback((groupKey, originalIndex, column, value) => {
        if (column === "dispatchQty") {
            onDispatchQtyChange?.(groupKey, originalIndex, value);
        }
    }, [onDispatchQtyChange]);

    // Filter function
    const filterFunction = useMemo(() => {
        return (row, _originalIndex, filters) => {
            const shownSO =
                row.salesOrder ??
                row.soNumberFromDetails ??
                (row.salesOrderId ? String(row.salesOrderId) : row.shipmentNumber ?? "");
            const visibleSO = normalizeSoValue(shownSO) ?? "";

            let destCity = row.soDestinationCity ?? null;
            let destState = row.soDestinationState ?? null;
            if (
                (!destCity || !destState) &&
                Array.isArray(row.soDetails) &&
                row.soDetails.length > 0
            ) {
                const sd = row.soDetails[0]?.destination ?? {};
                destCity = destCity ?? (sd?.city ?? sd?.City ?? null);
                destState = destState ?? (sd?.state ?? sd?.State ?? null);
            }

            const derived = {
                lineNo: row.lineNo ?? row.__originalIndex__ ?? "",
                salesOrder: visibleSO,
                soDestinationCity: destCity ?? "",
                soDestinationState: destState ?? "",
                skuId: row.skuId ?? "",
                description: row.description ?? "",
                pack: row.pack ?? "",
                uom: row.uom ?? "",
                orderedQty: row.orderedQty ?? row.expectedQty ?? "",
                itemPOTolerance: row.itemPOTolerance ?? "",
                openQty: row.openQty ?? "",
                dispatchQty: row.dispatchQty ?? "",
            };

            return Object.entries(filters).every(([k, filterVal]) => {
                if (!filterVal) return true;
                const val = derived[k];
                return String(val ?? "")
                    .toLowerCase()
                    .includes(String(filterVal).toLowerCase());
            });
        };
    }, []);

    // Header actions (Confirm Delivery button)
    const headerActions = useCallback(
        ({ groupKey, content, readOnly }) => {
            if (!showConfirmButton || typeof onConfirmDelivery !== "function" || readOnly) {
                return null;
            }

            return (
                <button
                    className="btn btn-sm btn-outline-primary"
                    title="Confirm Delivery (POD)"
                    onClick={(e) => {
                        e.stopPropagation();
                        const lines = Array.isArray(content) ? content : [];
                        const minimalRecord = {
                            shipmentDetails: {
                                shipmentNumber: lines[0]?.shipmentNumber ?? null,
                                _id: lines[0]?.shipmentId ?? null,
                            },
                            lineItems: lines,
                            _id: lines[0]?.shipmentId ?? null,
                        };
                        onConfirmDelivery?.(minimalRecord, { __openConfirmDelivery: true });
                    }}
                >
                    <i className="bi bi-arrow-right-circle me-1" /> Confirm Delivery
                </button>
            );
        },
        [onConfirmDelivery, showConfirmButton]
    );

    // Handle remove group
    const handleRemoveGroup = useCallback((groupKey) => {
        if (onRemoveSku) {
            onRemoveSku(groupKey);
        }
    }, [onRemoveSku]);

    // Clean data (remove __lastAddedTable)
    const cleanData = useMemo(() => {
        const clone = { ...data };
        if ("__lastAddedTable" in clone) delete clone.__lastAddedTable;
        return clone;
    }, [data]);

    return (
        <ItemsTable
            data={cleanData}
            columns={COLUMNS}
            columnConfigs={COLUMN_CONFIGS}
            filterPlaceholders={FILTER_PLACEHOLDERS}
            cellRenderer={cellRenderer}
            headerLabelFormatter={headerLabelFormatter}
            headerActions={headerActions}
            filterFunction={filterFunction}
            onCellChange={handleCellChange}
            onRemoveGroup={!readOnly ? handleRemoveGroup : null}
            onSelectionChange={onSelectionChange}
            readOnly={readOnly}
            autoSelectOnInput={true}
        />
    );
};

export default function StopsSection({ 
    formData, 
    onChange, 
    viewOnly = false, 
    isEdit = false,
    renderTablesBelow = false,
    loadTable: externalLoadTable = null,
    onDispatchQtyChange = null,
    onRemoveSku = null,
    onSelectionChange = null,
    onConfirmDelivery = null,
    onRemoveRow = null,
    showConfirmButton = false,
    createUsing = null,
}) {
    const dispatch = useDispatch();
    const reduxLoadTable = useSelector(state => state.skutable?.TableDetails || {});
    const loadTable = externalLoadTable || reduxLoadTable;
    const userInteractingRef = useRef(false);
    
    // State for managing stops
    const [stops, setStops] = useState(() => {
        // Initialize with first stop if orderNumbers exists in formData
        const initialOrderNumbers = formData?.orderNumbers || formData?.stops?.[0]?.orderNumber || "";
        if (initialOrderNumbers) {
            return [{ id: 1, orderNumber: String(initialOrderNumbers).trim() }];
        }
        // Check if formData has stops array
        if (Array.isArray(formData?.stops) && formData.stops.length > 0) {
            return formData.stops.map((stop, idx) => ({
                id: stop.id ?? stop.stopListNo ?? (idx + 1), // Preserve original id/stopListNo if available
                orderNumber: String(stop.orderNumber || stop || "").trim()
            }));
        }
        return [{ id: 1, orderNumber: "" }];
    });

    const isInitializedRef = React.useRef(false);

    // Sync stops with formData when it changes externally (only once on mount)
    React.useEffect(() => {
        if (isInitializedRef.current) return;
        
        const initialOrderNumbers = formData?.orderNumbers || "";
        const initialStops = formData?.stops;
        
        if (initialStops && Array.isArray(initialStops) && initialStops.length > 0) {
            isUpdatingFromExternalRef.current = true;
            setStops(initialStops.map((stop, idx) => ({
                id: stop.id ?? stop.stopListNo ?? (idx + 1), // Preserve original id/stopListNo if available
                orderNumber: String(stop.orderNumber || stop || "").trim()
            })));
            isInitializedRef.current = true;
        } else if (initialOrderNumbers && stops.length === 1 && !stops[0].orderNumber) {
            isUpdatingFromExternalRef.current = true;
            setStops([{ id: 1, orderNumber: String(initialOrderNumbers).trim() }]);
            isInitializedRef.current = true;
        } else {
            isInitializedRef.current = true;
        }
    }, []); // Only run once on mount

    // Update formData when stops change
    const prevStopsRef = React.useRef(JSON.stringify(stops));
    const isUpdatingFromExternalRef = React.useRef(false);
    const onChangeRef = React.useRef(onChange);
    const formDataRef = React.useRef(formData);
    
    // Keep refs updated
    React.useEffect(() => {
        onChangeRef.current = onChange;
        formDataRef.current = formData;
    }, [onChange, formData]);
    
    React.useEffect(() => {
        if (!isInitializedRef.current) return;
        if (isUpdatingFromExternalRef.current) {
            isUpdatingFromExternalRef.current = false;
            return;
        }
        
        // Only update if stops actually changed (deep comparison)
        const currentStopsStr = JSON.stringify(stops);
        if (prevStopsRef.current === currentStopsStr) return;
        
        // Check if the new stops are the same as what's in formData (to avoid loops)
        const formDataStopsStr = JSON.stringify(formDataRef.current?.stops || []);
        if (currentStopsStr === formDataStopsStr) {
            prevStopsRef.current = currentStopsStr;
            return;
        }
        
        prevStopsRef.current = currentStopsStr;
        
        if (typeof onChangeRef.current === 'function') {
            // Update orderNumbers with first stop for backward compatibility
            const firstStopValue = stops.length > 0 && stops[0].orderNumber 
                ? stops[0].orderNumber 
                : "";
            
            // Only call onChange if values are actually different
            const currentOrderNumbers = formDataRef.current?.orderNumbers || "";
            if (firstStopValue !== currentOrderNumbers) {
                onChangeRef.current({ target: { name: 'orderNumbers', value: firstStopValue } });
            }
            
            // Only call onChange if stops are actually different
            if (currentStopsStr !== formDataStopsStr) {
                onChangeRef.current({ target: { name: 'stops', value: stops } });
            }
        }
    }, [stops]);

    const handleStopChange = (id, value) => {
        setStops(prev => prev.map(stop => 
            stop.id === id ? { ...stop, orderNumber: value } : stop
        ));
    };

    const handleAddStop = () => {
        const nextId = stops.length > 0 ? Math.max(...stops.map(s => s.id)) + 1 : 2;
        setStops(prev => [...prev, { id: nextId, orderNumber: "" }]);
    };

    const handleRemoveStop = (id) => {
        if (stops.length <= 1) {
            toast.warning("At least one stop is required");
            return;
        }
        
        // Remove the stop's table from loadTable
        const stopGroupKey = `STOP_${id}`;
        const updated = { ...(loadTable || {}) };
        if (updated[stopGroupKey]) {
            delete updated[stopGroupKey];
            dispatch(setLoadTable(updated));
        }
        
        setStops(prev => prev.filter(stop => stop.id !== id));
    };

    const handleSearch = async (stopId) => {
        if (viewOnly) return;

        const stop = stops.find(s => s.id === stopId);
        if (!stop || !stop.orderNumber || !stop.orderNumber.trim()) {
            toast.warning("Please enter an order number to search");
            return;
        }

        userInteractingRef.current = true;
        const searchValue = stop.orderNumber.trim();

        try {
            const apiResp = await getSearchBySkuPO(3, [searchValue]); // type 3 for SO/Order

            const blocks = Array.isArray(apiResp?.data)
                ? apiResp.data
                : Array.isArray(apiResp)
                    ? apiResp
                    : [];

            if (!blocks.length) {
                toast.warning("No results found");
                userInteractingRef.current = false;
                return;
            }

            // 🔑 Create a unique group key for this stop (one table per stop)
            const stopGroupKey = `STOP_${stopId}`;

            // 🔐 GLOBAL DEDUPE SET (single source of truth) - check across ALL stops
            const globalDedupeSet = new Set();

            Object.values(loadTable || {}).forEach((rows) => {
                if (!Array.isArray(rows)) return;

                rows.forEach((r) => {
                    const key = buildDedupeKey({
                        sku: r.skuId || r.item,
                        po: r.purchaseOrder || r.poNumber,
                        so: r.orderNumber || r.soNumber || r.salesOrder,
                    });
                    if (key) globalDedupeSet.add(key);
                });
            });

            const updated = { ...(loadTable || {}) };
            const pad4 = (n) => String(n).padStart(4, "0");

            // Get existing items for this stop's table (if any)
            const existingArr = Array.isArray(updated[stopGroupKey])
                ? [...updated[stopGroupKey]]
                : [];

            let totalAdded = 0;
            let duplicateCount = 0;

            // Process all blocks and add items to the stop's table
            let currentLineNumber = existingArr.length > 0
                ? Math.max(...existingArr.map(x => {
                    const lineNo = x.lineNo || x.lineNumber || 0;
                    return Number(String(lineNo).replace(/^0+/, '') || 0);
                }))
                : 0;

            for (const block of blocks) {
                const items = Array.isArray(block?.items) ? block.items : [];
                if (!items.length) continue;

                const toAdd = [];

                items.forEach((r) => {
                    const dedupeKey = buildDedupeKey({
                        sku: r.skuId || r.item,
                        po: r.purchaseOrder || r.poNumber,
                        so: r.orderNumber || r.soNumber || r.salesOrder,
                    });

                    // 🚫 DUPLICATE BLOCK - check globally across all stops
                    if (!dedupeKey || globalDedupeSet.has(dedupeKey)) {
                        duplicateCount++;
                        return;
                    }

                    globalDedupeSet.add(dedupeKey);

                    // Calculate next line number for this stop's table
                    currentLineNumber += 1;
                    const nextLine = currentLineNumber;

                    toAdd.push({
                        lineNo: pad4(nextLine),
                        purchaseOrder: r.purchaseOrder ?? r.poNumber ?? null,
                        purchaseOrderId: r.purchaseOrderId ?? null,
                        salesOrder: r.salesOrder ?? r.soNumber ?? r.orderNumber ?? null,
                        salesOrderId: r.salesOrderId ?? null,
                        shipmentNumber: r.shipmentNumber ?? null,
                        shipmentId: r.shipmentId ?? null,
                        skuId: r.skuId ?? r.item ?? "UNKNOWN",
                        item: r.item ?? r.title ?? null,
                        description: r.description ?? "",
                        pack: r.pack ?? "STD",
                        orderedQty: Number(r.orderedQty ?? r.orderedQuantity ?? r.orderQty ?? r.qtyOrdered ?? r.expectedQty ?? 0) || 0,
                        expectedQty: Number(r.expectedQty ?? r.orderedQty ?? r.orderedQuantity ?? r.orderQty ?? r.qtyOrdered ?? 0) || 0,
                        openQty: Number(r.openQty ?? r.orderedQty ?? r.expectedQty ?? 0) || 0,
                        dispatchQty: Number(r.dispatchQty ?? 0) || 0,
                        uom: r.uom ?? "EA",
                        qcRequired: r.qcRequired ?? null,
                        itemPOTolerance: r.itemPOTolerance ?? r.itemASNTolerance ?? r.tolerance ?? null,
                        createdAt: r.createdAt ?? null,
                        updatedAt: r.updatedAt ?? null,
                        holdCode: r.holdCode ?? null,
                        lpn: r.lpn ?? null,
                        cubes: r.itemCube ?? r.cube ?? null,
                        gross: r.itemWeight ?? r.grossWeight ?? null,
                        net: r.netWeight ?? null,
                        dateTime: r.dateTime ?? null,
                        rejected: r.rejected ?? 0,
                        soNumberFromDetails: r.soDetails?.[0]?.soNumber ?? null,
                        soDestinationCity: r.soDetails?.[0]?.destination?.city ?? r.soDetails?.[0]?.destination?.City ?? null,
                        soDestinationState: r.soDetails?.[0]?.destination?.state ?? r.soDetails?.[0]?.destination?.State ?? null,
                        soDetails: Array.isArray(r.soDetails) ? r.soDetails : null,
                        deliveryState: r.deliveryState ?? null,
                        _derived: r._derived ?? null,
                    });
                });

                if (toAdd.length > 0) {
                    existingArr.push(...toAdd);
                    totalAdded += toAdd.length;
                }
            }

            // Update the stop's table with all new items
            if (totalAdded > 0) {
                updated[stopGroupKey] = existingArr;
                updated.__lastAddedTable = stopGroupKey;
                dispatch(setLoadTable(updated));
                toast.success(`Added ${totalAdded} item(s) to Stop ${stopId}${duplicateCount > 0 ? ` (${duplicateCount} duplicate(s) skipped)` : ''}`);
            } else if (duplicateCount > 0) {
                toast.warning(`All items are duplicates`);
            } else {
                toast.warning("No items found to add");
            }
        } catch (error) {
            console.error("Search error:", error);
            toast.error(`Failed to fetch data: ${error?.message || "Unknown error"}`);
        } finally {
            setTimeout(() => {
                userInteractingRef.current = false;
            }, 600);
        }
    };

    // Filter loadTable to get table for a specific stop
    const getStopTable = (stopId) => {
        const stopGroupKey = `STOP_${stopId}`;
        const stopTableData = loadTable?.[stopGroupKey];
        if (!stopTableData || !Array.isArray(stopTableData) || stopTableData.length === 0) {
            return null;
        }
        return { [stopGroupKey]: stopTableData };
    };

    return (
        <div className="stops-section mb-4">
            <div className="d-flex align-items-center mb-3">
                <label className="form-label mb-0 me-3 fw-semibold">Stops</label>
            </div>

            {renderTablesBelow ? (
                // Render stops with tables below each stop
                <div className="stops-with-tables">
                    {stops.map((stop, index) => {
                        const stopTable = getStopTable(stop.id);
                        const isLastStop = index === stops.length - 1;
                        return (
                            <div key={stop.id} className="stop-item mb-4">
                                {/* Stop Input Field */}
                                <div className="row g-3 mb-3">
                                    <div className="col-12">
                                        <div className="d-flex  gap-2 justify-content-between">
                                            <div className="col-12 col-md-6 col-lg-4 p-0">
                                                <label className="form-label small text-muted">
                                                    {index === 0 ? "Stops" : `Stops ${index + 1}`}
                                                </label>
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={stop.orderNumber}
                                                        onChange={(e) => handleStopChange(stop.id, e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                handleSearch(stop.id);
                                                            }
                                                        }}
                                                        placeholder="Enter order number"
                                                        disabled={viewOnly}
                                                    />
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        type="button"
                                                        onClick={() => handleSearch(stop.id)}
                                                        disabled={viewOnly || !stop.orderNumber?.trim()}
                                                    >
                                                        <i className="bi bi-search"></i>
                                                    </button>
                                                    {stops.length > 1 && !viewOnly && (
                                                        <button
                                                            className="btn btn-outline-danger"
                                                            type="button"
                                                            onClick={() => handleRemoveStop(stop.id)}
                                                            title="Remove this stop"
                                                        >
                                                            <i className="bi bi-x"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {isLastStop && !viewOnly && (
                                                <div className="d-flex align-items-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={handleAddStop}
                                                        title="Add another stop"
                                                    >
                                                        <i className="bi bi-plus-circle me-1"></i>
                                                        Add Stops
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Table for this stop (if it exists) */}
                                {stopTable && (
                                    <div className="stop-table mb-3">
                                        <StopItemsTable
                                            data={stopTable}
                                            stopId={stop.id}
                                            stopOrderNumber={stop.orderNumber}
                                            onDispatchQtyChange={onDispatchQtyChange}
                                            createUsing={createUsing}
                                            onRemoveSku={onRemoveSku}
                                            onSelectionChange={onSelectionChange}
                                            onConfirmDelivery={onConfirmDelivery}
                                            onRemoveRow={onRemoveRow}
                                            readOnly={viewOnly}
                                            showConfirmButton={showConfirmButton}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                // Original layout: all stops in a row
                <div className="row g-3">
                    {stops.map((stop, index) => {
                        const isLastStop = index === stops.length - 1;
                        return (
                            <div key={stop.id} className="col-12 col-md-6 col-lg-4">
                                <div className="d-flex gap-2 justify-content-between">
                                    <div className="flex-grow-1">
                                        <label className="form-label small text-muted">
                                            {index === 0 ? "Stops" : `Stops ${index + 1}`}
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={stop.orderNumber}
                                                onChange={(e) => handleStopChange(stop.id, e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleSearch(stop.id);
                                                    }
                                                }}
                                                placeholder="Enter order number"
                                                disabled={viewOnly}
                                            />
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => handleSearch(stop.id)}
                                                disabled={viewOnly || !stop.orderNumber?.trim()}
                                            >
                                                <i className="bi bi-search"></i>
                                            </button>
                                            {stops.length > 1 && !viewOnly && (
                                                <button
                                                    className="btn btn-outline-danger"
                                                    type="button"
                                                    onClick={() => handleRemoveStop(stop.id)}
                                                    title="Remove this stop"
                                                >
                                                    <i className="bi bi-x"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {isLastStop && !viewOnly && (
                                        <div className="d-flex align-items-end">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={handleAddStop}
                                                title="Add another stop"
                                            >
                                                <i className="bi bi-plus-circle me-1"></i>
                                                Add Stops
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

